(function () {
  const SELECTORS = {
    root: ".ta-locator",
    input: ".ta-locator__input",
    button: ".ta-locator__btn",
    list: ".ta-locator__list",
    status: ".ta-locator__status",
  };

  function loadScriptOnce(src) {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) return resolve();

      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }

      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.defer = true;
      s.addEventListener("load", resolve, { once: true });
      s.addEventListener("error", reject, { once: true });
      document.head.appendChild(s);
    });
  }

  function formatMiles(mi) {
    if (!isFinite(mi)) return "";
    return `${Math.round(mi * 10) / 10} mi`;
  }

  // Haversine distance (miles)
  function distanceMiles(a, b) {
    const R = 3958.7613;
    const toRad = (d) => (d * Math.PI) / 180;

    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);

    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);

    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);

    const h =
      sinDLat * sinDLat +
      Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;

    const c = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
    return R * c;
  }

  function safeText(v) {
    return (v ?? "").toString().trim();
  }

  function setStatus(root, msg) {
    const el = root.querySelector(SELECTORS.status);
    if (el) el.textContent = msg || "";
  }

  function buildInfoHtml(store) {
    const name = safeText(store.properties?.name);
    const address = safeText(store.properties?.address);
    const phone = safeText(store.properties?.phone);
    const website = safeText(store.properties?.website);
    const description = safeText(store.properties?.description);

    const websiteHref =
      website && !/^https?:\/\//i.test(website) ? `https://${website}` : website;

    return `
      <div style="padding:10px 12px; max-width:280px;">
        <div style="font-weight:700; font-size:16px; margin-bottom:6px;">${name}</div>
        ${address ? `<div style="font-size:13px; margin-bottom:6px;">${address}</div>` : ""}
        ${description ? `<div style="font-size:13px; margin-bottom:8px; color: rgba(0,0,0,.75);">${description}</div>` : ""}
        ${phone ? `<div style="font-size:13px; margin-bottom:6px;"><a href="tel:${phone.replace(/[^\d+]/g, "")}" style="color:#000; text-decoration:underline;">${phone}</a></div>` : ""}
        ${websiteHref ? `<div style="font-size:13px;"><a href="${websiteHref}" target="_blank" rel="noopener" style="color:#000; text-decoration:underline;">${website}</a></div>` : ""}
      </div>
    `;
  }

  async function fetchStores(url) {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`Failed to load stores JSON (${res.status})`);

    const json = await res.json();
    const features = Array.isArray(json?.features) ? json.features : [];

    return features
      .filter(
        (f) =>
          f?.geometry?.type === "Point" &&
          Array.isArray(f?.geometry?.coordinates)
      )
      .map((f) => {
        const [lng, lat] = f.geometry.coordinates; // GeoJSON: [lng, lat]
        return {
          ...f,
          __pos: { lat: Number(lat), lng: Number(lng) },
        };
      })
      .filter((f) => Number.isFinite(f.__pos.lat) && Number.isFinite(f.__pos.lng));
  }

  function clearActive(listEl) {
    listEl.querySelectorAll(".ta-locator__card.is-active").forEach((n) => {
      n.classList.remove("is-active");
    });
  }

  function renderList({ listEl, stores, onPick, limit }) {
    listEl.innerHTML = "";

    const slice = stores.slice(0, limit);

    slice.forEach((store) => {
      const name = safeText(store.properties?.name);
      const address = safeText(store.properties?.address);
      const phone = safeText(store.properties?.phone);
      const website = safeText(store.properties?.website);

      const websiteHref =
        website && !/^https?:\/\//i.test(website) ? `https://${website}` : website;

      const distanceText =
        store.__distanceMiles != null ? formatMiles(store.__distanceMiles) : "";

      const card = document.createElement("div");
      card.className = "ta-locator__card";
      card.setAttribute("role", "listitem");

      card.innerHTML = `
        <div class="ta-locator__pin" aria-hidden="true"><div class="ta-locator__pin-dot"></div></div>
        <div>
          ${distanceText ? `<div class="ta-locator__distance">${distanceText}</div>` : ""}
          <p class="ta-locator__name">${name}</p>
          ${address ? `<p class="ta-locator__row ta-locator__muted">${address}</p>` : ""}
          ${phone ? `<p class="ta-locator__row"><a class="ta-locator__link" href="tel:${phone.replace(/[^\d+]/g, "")}">${phone}</a></p>` : ""}
          ${websiteHref ? `<p class="ta-locator__row"><a class="ta-locator__link" href="${websiteHref}" target="_blank" rel="noopener">${website}</a></p>` : ""}
        </div>
      `;

      card.addEventListener("click", () => onPick(store, card));
      listEl.appendChild(card);
    });

    if (slice.length === 0) {
      const empty = document.createElement("div");
      empty.className = "ta-locator__card";
      empty.innerHTML = `<div></div><div><p class="ta-locator__row">No stores found.</p></div>`;
      listEl.appendChild(empty);
    }
  }

  async function geocodeAddress(geocoder, address) {
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status !== "OK" || !results?.[0]?.geometry?.location) {
          reject(new Error(`Geocoding failed: ${status}`));
          return;
        }
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      });
    });
  }

  async function initLocator(root) {
    const storesUrl = root.dataset.storesUrl;
    const apiKey = root.dataset.googleKey;
    const mapId = root.dataset.mapId || "";
    const limit = parseInt(root.dataset.resultsLimit || "25", 10);

    // lat/lng come as strings because schema uses "text"
    const defaultLat = parseFloat(root.dataset.defaultLat || "0");
    const defaultLng = parseFloat(root.dataset.defaultLng || "0");
    const defaultZoom = parseInt(root.dataset.defaultZoom || "4", 10);

    const input = root.querySelector(SELECTORS.input);
    const button = root.querySelector(SELECTORS.button);
    const listEl = root.querySelector(SELECTORS.list);

    if (!apiKey) {
      setStatus(root, "Missing Google Maps API key in section settings.");
      return;
    }
    if (!storesUrl) {
      setStatus(root, "Missing stores JSON URL.");
      return;
    }

    setStatus(root, "Loading stores…");
    const stores = await fetchStores(storesUrl);
    setStatus(root, `${stores.length} locations loaded.`);

    const mapsSrc = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=marker`;
    await loadScriptOnce(mapsSrc);

    const mapEl =
      root.querySelector(`#ta-locator-map-${CSS.escape(root.dataset.sectionId || "")}`) ||
      root.querySelector(".ta-locator__map");

    const fallbackCenter = {
      lat: Number.isFinite(defaultLat) ? defaultLat : 0,
      lng: Number.isFinite(defaultLng) ? defaultLng : 0,
    };

    const map = new google.maps.Map(mapEl, {
      center: fallbackCenter,
      zoom: Number.isFinite(defaultZoom) ? defaultZoom : 4,
      mapId: mapId || undefined,
      fullscreenControl: true,
      streetViewControl: false,
      mapTypeControl: false,
    });

    const infoWindow = new google.maps.InfoWindow();

    // Markers (same order as "stores")
    const markers = stores.map((store) => {
      const marker = new google.maps.Marker({
        map,
        position: store.__pos,
        title: safeText(store.properties?.name),
      });

      marker.addListener("click", () => {
        infoWindow.setContent(buildInfoHtml(store));
        infoWindow.open({ map, anchor: marker });
      });

      return marker;
    });

    // Initial list (no distance)
    renderList({
      listEl,
      stores,
      limit,
      onPick: (store, card) => {
        clearActive(listEl);
        card.classList.add("is-active");

        map.panTo(store.__pos);
        map.setZoom(Math.max(map.getZoom(), 12));

        infoWindow.setContent(buildInfoHtml(store));
        infoWindow.setPosition(store.__pos);
        infoWindow.open(map);
      },
    });

    const geocoder = new google.maps.Geocoder();

    async function runSearch() {
      const q = safeText(input.value);
      if (!q) {
        setStatus(root, "Type a zipcode or address.");
        return;
      }

      setStatus(root, "Searching…");

      let origin;
      try {
        origin = await geocodeAddress(geocoder, q);
      } catch (e) {
        setStatus(root, "We couldn't find that location. Try a different address/zip.");
        return;
      }

      // rank by distance
      const ranked = stores
        .map((s) => ({
          ...s,
          __distanceMiles: distanceMiles(origin, s.__pos),
        }))
        .sort((a, b) => a.__distanceMiles - b.__distanceMiles);

      setStatus(root, `Showing nearest locations to "${q}".`);

      renderList({
        listEl,
        stores: ranked,
        limit,
        onPick: (store, card) => {
          clearActive(listEl);
          card.classList.add("is-active");

          map.panTo(store.__pos);
          map.setZoom(12);

          infoWindow.setContent(buildInfoHtml(store));
          infoWindow.setPosition(store.__pos);
          infoWindow.open(map);
        },
      });

      map.setCenter(origin);
      map.setZoom(9);

      // origin marker (optional)
      if (!runSearch.__originMarker) {
        runSearch.__originMarker = new google.maps.Marker({
          map,
          position: origin,
          title: "Search location",
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 6,
            strokeWeight: 2,
          },
        });
      } else {
        runSearch.__originMarker.setPosition(origin);
        runSearch.__originMarker.setMap(map);
      }
    }

    button.addEventListener("click", runSearch);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") runSearch();
    });
  }

  function boot() {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (root.__taLocatorInit) return;
      root.__taLocatorInit = true;

      initLocator(root).catch((e) => {
        console.error("[TA Locator] init error", e);
        setStatus(root, "Something went wrong loading the locator.");
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();